import { z } from 'zod';

/**
 * The optional phone number asked for wherever an account is created.
 *
 * Deliberately lenient: this is a contact note for the new company, not a
 * number we will text, so the job is to catch typos (a bare seven-digit local
 * number with no area code, letters, a stray digit missing) and to accept
 * every way people write a number. Texting has its own stricter rules in
 * `phone.ts`, applied wherever a number is actually used to send.
 *
 * Accepted: an optional country code (`+1`, `+44`, `001`, or a bare leading
 * `1`), then the area code and the rest of the number — at least ten digits in
 * all, at most fifteen (the E.164 ceiling) — with spaces, dashes, dots and
 * parentheses anywhere. A `+` is allowed only at the very start.
 */
export const SIGNUP_PHONE_MAX_LENGTH = 40;
const MIN_DIGITS = 10;
const MAX_DIGITS = 15;

export const SIGNUP_PHONE_MESSAGE = 'Enter a phone number with its area code, like (555) 123-4567';

export function isAcceptableSignupPhone(raw: string): boolean {
  const trimmed = raw.trim();
  if (!/^\+?[\d\s().-]+$/.test(trimmed)) return false;
  const digits = trimmed.replace(/\D/g, '');
  // `00` is the international call prefix most of the world dials in place of `+`.
  const significant = !trimmed.startsWith('+') && digits.startsWith('00') ? digits.slice(2) : digits;
  return significant.length >= MIN_DIGITS && significant.length <= MAX_DIGITS;
}

/** Blank or absent becomes `undefined` (nothing to store); anything typed must pass. */
export const signupPhoneSchema = z
  .string()
  .trim()
  .max(SIGNUP_PHONE_MAX_LENGTH, SIGNUP_PHONE_MESSAGE)
  .nullish()
  .superRefine((value, ctx) => {
    if (value && !isAcceptableSignupPhone(value)) {
      ctx.addIssue({ code: 'custom', message: SIGNUP_PHONE_MESSAGE });
    }
  })
  .transform((value) => value || undefined);
